import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ModelImageSelection from './ModelImageSelection';
import PoseSelection from './PoseSelection';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import { tryonExamplePresets, AIPosesExamplePreset } from './examplePresets';
import { getSubscriptionStatus, SubscriptionType, isProPlan, isEnterprisePlan } from '../../services/subscriptionService';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import CustomSelect from '../ui/CustomSelect';

interface AIPosesSettings {
    background: string;
    expression: string;
    variations: number;
    prompt: string;
}

interface LocationState {
    inputImages: string[];
    prompt: string;
    background: string;
    expression: string;
    variations: number;
    recreatingFrom: string;
    generatedImages?: string[];
}

interface PoseItem {
    poseId: string;
    imageUrl: string[];
    prompt: string;
    type: string;
    gender: string;
    generationType: string;
    timestamp: string;
    liveStatus?: boolean;
    size?: string;
}

interface AIPosesApiPayload {
    background: string;
    colorTheme: string;
    expression: string;
    modelImage: {
        id: string;
        imageUrl: string[];
    };
    pose?: PoseItem | null;  // Keep for backward compatibility (single pose)
    poses?: PoseItem[] | null;  // Add for multiple poses
    method: string;
    aspectRatio: string;
    viewType: string;
    imageSize: string;
    prompt: string;
    numImages: number;
    numInferenceSteps: number;
    guidanceScale: number;
    outputFormat: string;
    enableSafetyChecker: boolean;
    userId: string;
    mode: string;
    optimizeCredits?: boolean;  // Add optimizeCredits flag
}

// Background options
const BACKGROUND_OPTIONS = [
    { value: 'no-change', label: 'No Change' },
    { value: 'natural', label: 'Natural' },
    { value: 'studio', label: 'Studio' },
    { value: 'outdoor', label: 'Outdoor' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'simple-background', label: 'Simple Background' }
];

// Background color options (for when simple-background is selected)
const BACKGROUND_COLOR_OPTIONS = [
    { id: 'white', name: 'White', color: '#ffffff', description: 'Clean white background', value: 'white' },
    { id: 'black', name: 'Black', color: '#000000', description: 'Solid black background', value: 'black' },
    { id: 'cream', name: 'Cream', color: '#f5f5dc', description: 'Pastel cream background', value: 'pastel cream' },
    { id: 'red', name: 'Red', color: '#ffb3ba', description: 'Pastel red background', value: 'pastel red' },
    { id: 'yellow', name: 'Yellow', color: '#ffdfba', description: 'Pastel yellow background', value: 'pastel yellow' },
    { id: 'brown', name: 'Brown', color: '#d4a574', description: 'Pastel brown background', value: 'pastel brown' },
    { id: 'grey', name: 'Grey', color: '#d3d3d3', description: 'Pastel grey background', value: 'pastel grey' }
];

// Expression options
const EXPRESSION_OPTIONS = [
    { value: 'no-change', label: 'No Change' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'smiling', label: 'Smiling' },
    { value: 'laughing', label: 'Laughing' }
];

const AIPoses: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as LocationState;

    const [inputImageUrls, setinputImageUrls] = useState<string[]>([]);
    const [selectedPoseIds, setSelectedPoseIds] = useState<string[]>([]);
    const [optimizeCredits, setOptimizeCredits] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [generatedImages, setGeneratedImages] = useState<string[]>(state?.generatedImages || []);
    const [generationTime, setGenerationTime] = useState<number>(0);
    const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
    const { currentUser, refreshUserCredits, userCredits } = useAuth();

    // Settings state
    const [settings, setSettings] = useState<AIPosesSettings>({
        background: state?.background || 'no-change',
        expression: state?.expression || 'no-change',
        variations: state?.variations || 1,
        prompt: state?.prompt || ''
    });

    const [mode, setMode] = useState<string>('performance');
    const [selectedBackground, setSelectedBackground] = useState<string>('white');
    const [selectedView, setSelectedView] = useState<string>('random');
    const [isExample, setIsExample] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [aspectRatio, setAspectRatio] = useState<string>('9:16');
    const [hasVideoAccess, setHasVideoAccess] = useState<boolean>(false);
    const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
    const [genderFilter, setGenderFilter] = useState<string>('female');
    const [poseTypeFilter, setPoseTypeFilter] = useState<string>('standing');
    const [sizeFilter, setSizeFilter] = useState<string>('normal');

    // Credit estimation hook
    const [creditEstimation, estimateCreditCost] = useCreditEstimation('ai-poses');

    // Mode restrictions hook
    const modeRestrictions = useModeRestrictions();

    // Effect to handle recreation state
    useEffect(() => {
        const loadInputImages = async () => {
            if (state?.inputImages && state.inputImages.length >= 1) {
                try {
                    // Load model image (first priority)
                    setinputImageUrls([state.inputImages[0]]);

                    // Set generated images if they exist
                    if (state.generatedImages) {
                        setGeneratedImages(state.generatedImages);
                    }
                } catch (error) {
                    console.error('Failed to load input images:', error);
                }
            }
        };

        loadInputImages();
    }, [state]);

    // Clear error messages when user uploads new images
    useEffect(() => {
        if (inputImageUrls.length > 0) {
            setErrorMessage('');
        }
    }, [inputImageUrls]);

    // Disable pose selection when user writes a prompt
    useEffect(() => {
        if (settings.prompt && settings.prompt.trim().length > 0) {
            setSelectedPoseIds([]);
        }
    }, [settings.prompt]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
            }
        };
    }, [timerInterval]);

    // Check subscription status for video generation access
    useEffect(() => {
        const checkVideoAccess = async () => {
            if (!currentUser?.uid) {
                setHasVideoAccess(false);
                return;
            }

            try {
                const response = await getSubscriptionStatus(currentUser.uid);
                const hasAccess = isProPlan(response.type) || isEnterprisePlan(response.type);
                setHasVideoAccess(hasAccess);
            } catch (error) {
                console.error('Failed to check subscription status:', error);
                setHasVideoAccess(false);
            }
        };

        checkVideoAccess();
    }, [currentUser]);

    // Handle mode restrictions - automatically switch to available mode if current is restricted
    useEffect(() => {
        if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
            // Switch to the first available mode (Performance is always available)
            const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
            if (availableMode) {
                setMode(availableMode.value);
            }
        }
    }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions.canUseMode]);

    // Credit estimation effect - trigger when generation settings change
    useEffect(() => {
        // Only estimate if we have required data and user is authenticated
        if (!currentUser?.uid || inputImageUrls.length === 0) {
            return;
        }

        const estimateCost = async () => {
            // Fetch pose data for all selected poses
            let posesData: PoseItem[] = [];
            if (selectedPoseIds.length > 0) {
                try {
                    const poseResponse = await fetch(API_CONFIG.GET_POSE_PRESETS_URL);
                    const poseApiData = await poseResponse.json();

                    if (poseApiData.success && poseApiData.poses) {
                        posesData = selectedPoseIds.map(poseId => {
                            const poseData = poseApiData.poses.find((pose: any) => pose.poseId === poseId);
                            if (poseData) {
                                const imageUrl = Array.isArray(poseData.imageUrl) ? poseData.imageUrl[0] : poseData.imageUrl;
                                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                                return {
                                    poseId: poseData.poseId,
                                    imageUrl: [cleanImageUrl],
                                    prompt: poseData.prompt || "",
                                    type: poseData.type || "",
                                    gender: poseData.gender || "",
                                    generationType: poseData.generationType || "pose",
                                    liveStatus: poseData.liveStatus,
                                    timestamp: poseData.timestamp || new Date().toISOString(),
                                    size: poseData.size || "normal"
                                } as PoseItem;
                            }
                            return null;
                        }).filter((pose): pose is PoseItem => pose !== null);
                    }
                } catch (error) {
                    console.error('Failed to fetch pose data for credit estimation:', error);
                }
            }

            // Determine method
            let estimationMethod = selectedPoseIds.length > 0 ? "preselected" : "random";

            const estimationPayload = {
                background: settings.background,
                colorTheme: BACKGROUND_COLOR_OPTIONS.find(bg => bg.id === selectedBackground)?.value || 'white',
                expression: settings.expression,
                inputImageUrls: inputImageUrls,
                pose: selectedPoseIds.length === 1 ? posesData[0] : null,  // Single pose (backward compatibility)
                poses: selectedPoseIds.length > 1 ? posesData : null,  // Multiple poses
                method: estimationMethod,
                aspectRatio: aspectRatio,
                viewType: selectedView,
                imageSize: "square",
                prompt: settings.prompt || '',
                numImages: 1,  // Fixed to 1 for AI Poses
                numInferenceSteps: 28,
                guidanceScale: 3.5,
                outputFormat: "jpeg",
                enableSafetyChecker: true,
                mode: mode.toLowerCase(),
                quality: "high",
                optimizeCredits: optimizeCredits  // Add optimizeCredits flag
            };

            estimateCreditCost(estimationPayload);
        };

        estimateCost();
    }, [
        currentUser?.uid,
        inputImageUrls,
        settings.background,
        settings.expression,
        mode,
        selectedView,
        selectedPoseIds,
        optimizeCredits,
        aspectRatio,
        estimateCreditCost
    ]);

    const startTimer = () => {
        setGenerationTime(0);
        const interval = setInterval(() => {
            setGenerationTime(prev => prev + 0.1);
        }, 100);
        setTimerInterval(interval);
    };

    const stopTimer = () => {
        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }
    };

    const handleGenerate = async () => {
        if (inputImageUrls.length === 0) {
            alert('Please upload a model image');
            return;
        }

        // Check if user can afford the generation
        if (!creditEstimation.canAfford) {
            setShowInsufficientCreditsModal(true);
            return;
        }

        setIsProcessing(true);
        setGeneratedImages([]);
        setIsExample(false);
        setErrorMessage('');
        startTimer(); // Start the timer

        try {
            // Fetch pose data for all selected poses
            let posesData: PoseItem[] = [];
            if (selectedPoseIds.length > 0) {
                try {
                    const poseResponse = await fetch(API_CONFIG.GET_POSE_PRESETS_URL);
                    const poseApiData = await poseResponse.json();

                    if (poseApiData.success && poseApiData.poses) {
                        posesData = selectedPoseIds.map(poseId => {
                            const poseData = poseApiData.poses.find((pose: any) => pose.poseId === poseId);
                            if (poseData) {
                                const imageUrl = Array.isArray(poseData.imageUrl) ? poseData.imageUrl[0] : poseData.imageUrl;
                                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                                return {
                                    poseId: poseData.poseId,
                                    imageUrl: [cleanImageUrl],
                                    prompt: poseData.prompt || "",
                                    type: poseData.type || "",
                                    gender: poseData.gender || "",
                                    generationType: poseData.generationType || "pose",
                                    liveStatus: poseData.liveStatus,
                                    timestamp: poseData.timestamp || new Date().toISOString(),
                                    size: poseData.size || "normal"
                                } as PoseItem;
                            }
                            return null;
                        }).filter((pose): pose is PoseItem => pose !== null);
                    }
                } catch (error) {
                    console.error('Failed to fetch pose data:', error);
                }
            }

            // Determine method
            let method = selectedPoseIds.length > 0 ? "preselected" : "random";

            const payload = {
                background: settings.background,
                colorTheme: BACKGROUND_COLOR_OPTIONS.find(bg => bg.id === selectedBackground)?.value || 'white',
                expression: settings.expression,
                inputImageUrls: inputImageUrls,
                pose: selectedPoseIds.length === 1 ? posesData[0] : null,  // Single pose (backward compatibility)
                poses: selectedPoseIds.length > 1 ? posesData : null,  // Multiple poses
                method: method,
                aspectRatio: aspectRatio,
                viewType: selectedView,
                imageSize: "square",
                prompt: settings.prompt || '',
                numImages: 1,
                numInferenceSteps: 28,
                guidanceScale: 3.5,
                outputFormat: "jpeg",
                enableSafetyChecker: true,
                userId: currentUser?.uid || "user_id",
                mode: mode.toLowerCase(),
                quality: "high",
                optimizeCredits: optimizeCredits  // Add optimizeCredits flag
            };

            // Use AI Poses generation endpoint
            const response = await fetch(API_CONFIG.AI_POSES_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            // Check HTTP status
            if (!response.ok) {
                setErrorMessage('Something went wrong. Please try again.');
                return;
            }

            const data = await response.json();

            // Check if images were returned
            if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
                setGeneratedImages(data.imageUrls);
                // Refresh user credits after successful generation
                await refreshUserCredits();
                setErrorMessage('');
            } else {
                setErrorMessage('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Failed to generate AI poses:', error);
            setErrorMessage('Failed to generate AI poses. Please try again.');
        } finally {
            stopTimer(); // Stop the timer
            setIsProcessing(false);
        }
    };

    // Example handling for model images
    const handleModelImageExampleSelect = (modelImageUrl: string, modelImageId: number) => {
        setIsExample(true);

        // Find matching preset based on model image URL
        const matchingPreset = tryonExamplePresets.find((preset: AIPosesExamplePreset) => preset.clothPreview === modelImageUrl);
        if (matchingPreset) {
            setSettings({ ...settings, prompt: matchingPreset.prompt });
            setGeneratedImages(matchingPreset.results);
        }
    };

    const handlePoseSelect = (poseUrl: string, poseId: string) => {
        // Pose selection handled by the PoseSelection component
        console.log('Pose selected:', poseId, poseUrl);
    };

    return (
        <div className="w-full relative">
            {/* Main Content Area - Three Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
                {/* First Column - Model Image Upload */}
                <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
                    <div className="p-4 border-b border-accent-taupe flex-shrink-0">
                        <h2 className="text-lg font-semibold text-text-heading">Model Image</h2>
                        <span className="text-sm text-text-description">Upload a model image to get started</span>
                    </div>

                    <div className="p-4 flex flex-col min-h-[400px]">
                        <ModelImageSelection
                            imageUrls={inputImageUrls}
                            setImageUrls={setinputImageUrls}
                            isExample={isExample}
                            onExampleSelect={handleModelImageExampleSelect}
                        />
                    </div>
                </div>

                {/* Second Column - Controls */}
                <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
                    <div className="p-4 border-b border-accent-taupe flex-shrink-0">
                        <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
                        <span className="text-sm text-text-description">Configure your AI poses settings</span>
                    </div>

                    <div className="p-4 space-y-3 flex-1 overflow-y-auto">


                        {/* AI Poses Selection */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                    <label className="text-xs font-medium text-text-heading">AI Poses</label>
                                </div>
                                <div className="flex space-x-2">
                                    {/* Gender Filter */}
                                    <CustomSelect
                                        value={genderFilter}
                                        onChange={(value) => setGenderFilter(value)}
                                        options={[
                                            { value: 'male', label: 'Male' },
                                            { value: 'female', label: 'Female' }
                                        ]}
                                        className="w-full"
                                    />
                                    {/* Pose Type Filter */}
                                    <CustomSelect
                                        value={poseTypeFilter}
                                        onChange={(value) => setPoseTypeFilter(value)}
                                        options={[
                                            { value: 'standing', label: 'Standing' },
                                            { value: 'sitting', label: 'Sitting' },
                                            { value: 'leaning', label: 'Leaning' },
                                            { value: 'back', label: 'Back' }
                                        ]}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            <div className="bg-accent-sand rounded-lg p-2">
                                <PoseSelection
                                    selectedPoseIds={selectedPoseIds}
                                    setSelectedPoseIds={setSelectedPoseIds}
                                    onPoseSelect={handlePoseSelect}
                                    disabled={Boolean(settings.prompt && settings.prompt.trim().length > 0)}
                                    genderFilter={genderFilter}
                                    poseTypeFilter={poseTypeFilter}
                                    sizeFilter={sizeFilter}
                                    onGenderFilterChange={setGenderFilter}
                                    onPoseTypeFilterChange={setPoseTypeFilter}
                                    onSizeFilterChange={setSizeFilter}
                                />
                            </div>
                        </div>

                        {/* Optimize Credits Checkbox */}
                        <div className={`bg-accent-sand rounded-lg p-3 border border-accent-taupe ${selectedPoseIds.length < 2 ? 'opacity-60' : ''}`}>
                            <label className={`flex items-center space-x-2 ${selectedPoseIds.length >= 2 ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                <input
                                    type="checkbox"
                                    checked={optimizeCredits}
                                    onChange={(e) => setOptimizeCredits(e.target.checked)}
                                    disabled={selectedPoseIds.length < 2}
                                    className="w-4 h-4 text-secondary-600 border-accent-taupe rounded focus:ring-secondary-500 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                                <div className="flex-1">
                                    <span className="text-sm font-medium text-text-heading">Optimize Credits</span>
                                    <p className="text-xs text-text-description mt-0.5">
                                        {selectedPoseIds.length < 2
                                            ? "Select 2 or more poses to enable credit optimization"
                                            : optimizeCredits
                                                ? `Generate ${selectedPoseIds.length} poses (1 credit per 4 poses)`
                                                : `Generate ${selectedPoseIds.length} poses individually (${selectedPoseIds.length} credits)`
                                        }
                                    </p>
                                </div>
                            </label>
                        </div>

                        {/* Advanced Settings */}
                        <div className="border-t border-accent-taupe pt-2">
                            <button
                                type="button"
                                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                                className="flex items-center justify-between w-full text-left text-xs font-medium text-text-heading hover:text-text-heading transition-colors"
                            >
                                <span className="flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Advanced Settings
                                </span>
                                <svg
                                    className={`w-3 h-3 transition-transform duration-200 ${showAdvancedSettings ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {showAdvancedSettings && (
                                <div className="mt-4 space-y-4">
                                    {/* Compact Controls Grid */}
                                    <div className="space-y-3">
                                        {/* First Row - Background, Expression, Mode */}
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">
                                                    Background
                                                    {settings.prompt && settings.prompt.trim().length > 0 && (
                                                        <span className="ml-1 text-xs text-text-description">(Disabled)</span>
                                                    )}
                                                </label>
                                                <CustomSelect
                                                    value={settings.background}
                                                    onChange={(value) => setSettings({ ...settings, background: value })}
                                                    options={BACKGROUND_OPTIONS.map((option) => ({
                                                        value: option.value,
                                                        label: option.label
                                                    }))}
                                                    disabled={Boolean(settings.prompt && settings.prompt.trim().length > 0)}
                                                    className="w-full"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">
                                                    Expression
                                                    {settings.prompt && settings.prompt.trim().length > 0 && (
                                                        <span className="ml-1 text-xs text-text-description">(Disabled)</span>
                                                    )}
                                                </label>
                                                <CustomSelect
                                                    value={settings.expression}
                                                    onChange={(value) => setSettings({ ...settings, expression: value })}
                                                    options={EXPRESSION_OPTIONS.map((option) => ({
                                                        value: option.value,
                                                        label: option.label
                                                    }))}
                                                    disabled={Boolean(settings.prompt && settings.prompt.trim().length > 0)}
                                                    className="w-full"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">
                                                    Mode
                                                    {!modeRestrictions.hasInitialLoad && (
                                                        <span className="ml-1 text-xs text-text-description">(Loading...)</span>
                                                    )}
                                                </label>
                                                <CustomSelect
                                                    value={mode}
                                                    onChange={(value) => setMode(value)}
                                                    options={modeRestrictions.availableModes.map((modeOption) => {
                                                        let planRequirement = '';
                                                        if (modeOption.disabled && modeOption.requiresPlan) {
                                                            const minPlan = modeOption.requiresPlan[0];
                                                            switch (minPlan) {
                                                                case 'plus':
                                                                    planRequirement = ' (Plus)';
                                                                    break;
                                                                case 'pro':
                                                                    planRequirement = ' (Pro)';
                                                                    break;
                                                                case 'enterprise':
                                                                    planRequirement = ' (Ent)';
                                                                    break;
                                                                default:
                                                                    planRequirement = ' (Upgrade)';
                                                            }
                                                        }
                                                        return {
                                                            value: modeOption.value,
                                                            label: `${modeOption.label}${planRequirement}`,
                                                            disabled: modeOption.disabled
                                                        };
                                                    })}
                                                    disabled={!modeRestrictions.hasInitialLoad}
                                                    className="w-full"
                                                />
                                                {modeRestrictions.error && (
                                                    <p className="text-xs text-red-600 mt-1">{modeRestrictions.error}</p>
                                                )}
                                                {!modeRestrictions.canUseMode(mode) && modeRestrictions.hasInitialLoad && (
                                                    <p className="text-xs text-orange-600 mt-1">
                                                        <i className="fas fa-exclamation-triangle mr-1"></i>
                                                        {modeRestrictions.getRestrictedModeMessage(mode)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Second Row - View, Aspect Ratio, Model Size */}
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">View</label>
                                                <CustomSelect
                                                    value={selectedView}
                                                    onChange={(value) => setSelectedView(value)}
                                                    options={[
                                                        { value: 'random', label: 'Random' },
                                                        { value: 'full body view', label: 'Full-Body' },
                                                        { value: 'upper body view', label: 'Upper-Body' },
                                                        { value: 'closeup view', label: 'Closeup' },
                                                        { value: 'side view', label: 'Side View' },
                                                        { value: 'back view', label: 'Back View' }
                                                    ]}
                                                    className="w-full"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">Aspect Ratio</label>
                                                <CustomSelect
                                                    value={aspectRatio}
                                                    onChange={(value) => setAspectRatio(value)}
                                                    options={[
                                                        { value: '9:16', label: 'Portrait (9:16)' },
                                                        { value: '16:9', label: 'Landscape (16:9)' },
                                                        { value: '1:1', label: 'Square (1:1)' }
                                                    ]}
                                                    className="w-full"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">Model Size</label>
                                                <CustomSelect
                                                    value={sizeFilter}
                                                    onChange={(value) => setSizeFilter(value)}
                                                    options={[
                                                        { value: 'normal', label: 'Normal' },
                                                        { value: 'plus', label: 'Plus' }
                                                    ]}
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>

                                        {/* Background Color Selector - Compact version */}
                                        {settings.background === 'simple-background' && (
                                            <div>
                                                <label className="block text-xs font-medium text-text-heading mb-1">
                                                    Background Color
                                                    {settings.prompt && settings.prompt.trim().length > 0 && (
                                                        <span className="ml-1 text-xs text-text-description">(Disabled)</span>
                                                    )}
                                                </label>
                                                <div className={`grid grid-cols-7 gap-1 ${settings.prompt && settings.prompt.trim().length > 0 ? 'opacity-50 pointer-events-none' : ''
                                                    }`}>
                                                    {BACKGROUND_COLOR_OPTIONS.map((bg) => (
                                                        <button
                                                            key={bg.id}
                                                            onClick={() => setSelectedBackground(bg.id)}
                                                            disabled={Boolean(settings.prompt && settings.prompt.trim().length > 0)}
                                                            className={`relative h-8 rounded-md border-2 transition-all duration-200 group ${selectedBackground === bg.id
                                                                ? 'border-secondary-500 ring-2 ring-secondary-200'
                                                                : 'border-accent-taupe hover:border-accent-taupe'
                                                                }`}
                                                            style={{ backgroundColor: bg.color }}
                                                            title={`${bg.name} - ${bg.description}`}
                                                        >
                                                            {selectedBackground === bg.id && (
                                                                <div className="absolute top-0.5 right-0.5">
                                                                    <svg className="w-2 h-2 text-secondary-600 bg-white rounded-full" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                                <p className="text-xs text-text-description mt-1">
                                                    {BACKGROUND_COLOR_OPTIONS.find(bg => bg.id === selectedBackground)?.description}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-text-heading mb-1">Prompt</label>
                                        <textarea
                                            value={settings.prompt}
                                            onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
                                            placeholder="Add specific instructions for AI poses (e.g., confident pose in studio lighting with elegant expression)"
                                            className="w-full border border-accent-taupe rounded-lg px-2 py-2 text-sm focus:ring-secondary-500 focus:border-secondary-500 min-h-[60px] resize-y"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Credit Cost Preview */}
                        <div className="bg-accent-sand rounded-lg p-2">
                            <CreditCostPreview
                                creditCost={creditEstimation.creditCost}
                                isEstimating={creditEstimation.isEstimating}
                                error={creditEstimation.error}
                                canAfford={creditEstimation.canAfford}
                                className="text-xs"
                            />
                        </div>

                        {/* Clear Button */}
                        <button
                            onClick={() => {
                                // Clear pose selection
                                setSelectedPoseIds([]);
                                // Reset optimize credits
                                setOptimizeCredits(false);
                                // Clear prompt
                                setSettings({ ...settings, prompt: '' });
                                // Reset to default settings (keep input image)
                                setSettings(prev => ({
                                    ...prev,
                                    background: 'no-change',
                                    expression: 'no-change',
                                    variations: 1,
                                    prompt: ''
                                }));
                                // Reset filters to defaults
                                setGenderFilter('female');
                                setPoseTypeFilter('standing');
                                setSizeFilter('normal');
                                // Clear generated images
                                setGeneratedImages([]);
                                setIsExample(false);
                                setErrorMessage('');
                            }}
                            className="w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors bg-red-100 hover:bg-red-200 text-red-700 border border-red-300"
                            title="Clear all presets and prompts"
                        >
                            Clear All Settings
                        </button>

                        {/* Generate Button */}
                        <button
                            onClick={handleGenerate}
                            disabled={inputImageUrls.length === 0 || isProcessing}
                            className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${inputImageUrls.length === 0 || isProcessing
                                ? 'bg-gray-300 text-text-description cursor-not-allowed'
                                : 'bg-secondary-500 hover:bg-secondary-600 text-white'
                                }`}
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    <span>Processing...</span>
                                    <div className="flex items-center space-x-1 opacity-90">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-mono font-semibold text-xs">
                                            {generationTime.toFixed(1)}s
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                'Generate AI Poses'
                            )}
                        </button>

                        {/* Error message display */}
                        {errorMessage && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-xs text-red-700">{errorMessage}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Third Column - Generated Result */}
                <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
                    <div className="p-4 border-b border-accent-taupe flex-shrink-0">
                        <h2 className="text-lg font-semibold text-text-heading">Generated Result</h2>
                        <span className="text-sm text-text-description">
                            {isExample ? 'Example result will appear here' : 'Generated AI poses will appear here'}
                        </span>
                    </div>

                    <div className="p-4 flex-1 flex flex-col">
                        {isProcessing ? (
                            <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                                    <p className="text-sm text-text-description">Generating AI poses...</p>
                                    <p className="text-xs text-text-description mt-2">This may take a few moments</p>
                                </div>
                            </div>
                        ) : generatedImages.length > 0 ? (
                            <div className="relative flex-1 min-h-[400px] overflow-y-auto">
                                <div className={`${generatedImages.length === 1 ? 'grid grid-cols-1' : 'grid grid-cols-1 sm:grid-cols-2'} gap-2`}>
                                    {generatedImages.map((url, idx) => (
                                        <div key={idx} className="relative bg-accent-sand rounded-lg border border-accent-taupe overflow-hidden flex flex-col min-h-[300px]">
                                            <img
                                                src={url}
                                                alt={`${isExample ? 'Example' : 'Generated'} ${idx + 1}`}
                                                className="w-full h-[300px] object-contain"
                                            />
                                            <div className="absolute top-1 left-1 bg-secondary-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
                                                #{idx + 1}
                                            </div>
                                            {/* Action bar */}
                                            <div className="flex justify-center items-center gap-2 px-2 py-2 bg-white border-t border-accent-taupe">
                                                {/* Download Button */}
                                                <button
                                                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                                                    onClick={async () => {
                                                        try {
                                                            const response = await fetch(url);
                                                            const blob = await response.blob();
                                                            const blobUrl = window.URL.createObjectURL(blob);
                                                            const link = document.createElement('a');
                                                            link.href = blobUrl;
                                                            link.download = `${isExample ? 'example' : 'ai-poses'}-result-${idx + 1}.jpg`;
                                                            link.style.display = 'none';
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(blobUrl);
                                                        } catch (error) {
                                                            console.error('Download failed:', error);
                                                        }
                                                    }}
                                                >
                                                    Download
                                                </button>
                                                {/* Generate Video Button */}
                                                <button
                                                    className={`relative px-2 py-1 rounded-lg transition-colors text-xs font-medium border ${hasVideoAccess
                                                        ? 'text-text-description hover:text-secondary-600 hover:bg-secondary-100 border-accent-taupe hover:border-secondary-300'
                                                        : 'text-text-description cursor-not-allowed bg-accent-sand border-accent-taupe'
                                                        }`}
                                                    onClick={() => {
                                                        if (hasVideoAccess) {
                                                            navigate('/canvas-editor', {
                                                                state: {
                                                                    inputImages: [url],
                                                                    prompt: '',
                                                                    style: '',
                                                                    variations: 1,
                                                                    recreatingFrom: '',
                                                                }
                                                            });
                                                        } else {
                                                            navigate('/subscription');
                                                        }
                                                    }}
                                                >
                                                    Create Video
                                                    {!hasVideoAccess && (
                                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full flex items-center justify-center">
                                                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </button>
                                                {/* Upscale Button */}
                                                <button
                                                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                                                    onClick={() => {
                                                        navigate('/canvas-editor', {
                                                            state: {
                                                                inputImages: [url],
                                                                prompt: '',
                                                                style: '',
                                                                variations: 1,
                                                                recreatingFrom: '',
                                                                tool: 'upscaler'
                                                            }
                                                        });
                                                    }}
                                                >
                                                    HD
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                                <div className="text-center text-text-description">
                                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p className="text-sm">Upload a model image and click Generate to see results</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>



            {/* Insufficient Credits Modal */}
            <InsufficientCreditsModal
                isOpen={showInsufficientCreditsModal}
                onClose={() => setShowInsufficientCreditsModal(false)}
                requiredCredits={creditEstimation.creditCost || 0}
                availableCredits={userCredits}
                featureName="AI Poses Generation"
            />
        </div>
    );
};

export default AIPoses;
